import { use2FA } from "hooks/signup/use2FA";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useBitsUserContext } from "context/SignedInContext";
import { isCountryUs } from "utils/country";
import { SignupStep } from "../cassetteUI/signupFlow/signup/ui/SignupStep";
import { SignupField } from "../cassetteUI/signupFlow/signup/ui/SignupField";
import { SignupEnterSubmit, SignupSubmit } from "../cassetteUI/signupFlow/signup/ui/SignupSubmit";

const CODE_LENGTH = 4;

/** SMS step of the UK sign-in journey. */
export const MFA = () => {
  const {
    code,
    handleCodeChanged,
    handleSubmit,
    errorMessage,
    isSending,
    isResendAvailable,
    resendSMSVerificationCode,
    codeResent,
  } = use2FA();
  const navigate = useNavigate();
  const { phoneVerificationIdentifier } = useBitsUserContext();
  const { t, ready } = useTranslation();

  useEffect(() => {
    if (isCountryUs() || !phoneVerificationIdentifier) {
      navigate("/login");
    }
  }, []);

  if (!ready) {
    return null;
  }

  /* Length alone is not enough — it would accept four non-digits. */
  const isComplete = code?.length === CODE_LENGTH && /^\d+$/.test(code);

  return (
    <SignupStep
      id="login-container"
      title={t("auth.forms.login.smsCode.title")}
      subtitle={t("auth.forms.login.smsCode.subtitle")}
      actions={
        <SignupSubmit id="send-sms-btn" disabled={!isComplete} loading={isSending} onClick={handleSubmit}>
          {t("auth.forms.continue")}
        </SignupSubmit>
      }
      below={
        <>
          {/* A real button, not a link to "#": resending is an action on this
              page, not navigation, so it should not be reachable by middle
              click or announced as a link. */}
          {isResendAvailable && !isSending && (
            <button type="button" id="resend-sms-code" className="suResend" onClick={resendSMSVerificationCode}>
              <i className="bi bi-arrow-repeat suResend__icon" aria-hidden="true" />
              {t("auth.forms.login.smsCode.resend")}
            </button>
          )}
          {codeResent && (
            <p className="suNote suNote--positive" role="status">
              {t("auth.forms.login.smsCode.resent")}
            </p>
          )}
        </>
      }
    >
      <Form
        autoComplete="on"
        noValidate
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          if (isComplete && !isSending) {
            handleSubmit();
          }
        }}
      >
        <div className="suBody">
          {/* No visible label: the step title and subtitle above already name
              the field, and repeating them here read as the same sentence
              printed twice. The input carries the accessible name instead. */}
          <SignupField
            htmlFor="sms-code"
            error={errorMessage ? <span id="login-error-msg">{errorMessage}</span> : null}
          >
            {/* `inputMode` brings up the number pad and `one-time-code` lets
                iOS and Android offer the code straight from the SMS. */}
            <input
              type="text"
              name="sms-code"
              id="sms-code"
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={CODE_LENGTH}
              placeholder=""
              aria-label={t("auth.forms.login.smsCode.title")}
              /* Controlled, so a stripped character disappears from the field
                 too. Left uncontrolled, state and input would disagree. */
              value={code}
              onChange={handleCodeChanged}
              className="form-input suCode"
            />
          </SignupField>

          <SignupEnterSubmit />
        </div>
      </Form>
    </SignupStep>
  );
};
